import { useQuery } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Panel, Section, Stat, StateView, useAuth } from "@ozpanel/ui";

// Kernel & network tuning — the bespoke operational surface for the `hosttune` module.
//
// It is registered in MODULE_OPS rather than given its own route and nav entry. It had both,
// and because the module ALSO auto-navs from its Manifest.Title, the sidebar showed two
// identically-labelled "Kernel & network tuning" entries in the same group. A module that
// publishes a manifest already has a place in the navigation; adding a second by hand is how
// you get two.
//
// The whole point of this screen is the pair of columns: what ozpanel WROTE and what the kernel
// is ACTUALLY doing. On a container many sysctls are host-controlled — the file is written so a
// real node picks them up at boot, and they can never take effect here. A page that showed only
// the intended value would report tuning that does not exist, which is the failure this module
// was built to expose.

interface Tunable {
  key: string;
  label: string;
  group: string;
  live: string;
  stored?: string;
  default?: string;
  in_force: boolean;
  managed: boolean;
  at_ozpanel_default: boolean;
  readable: boolean;
  why: string;
}

export function TuningOps() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["hosttune-tunables"],
    queryFn: () =>
      client.apiFetch<{
        items: Tunable[] | null;
        total: number;
        not_in_force: number;
        managed_file: string;
        note?: string;
      }>("/hosttune/tunables"),
  });

  const d = q.data;
  const items = d?.items ?? [];
  const managed = items.filter((t) => t.managed).length;
  const offDefault = items.filter((t) => t.managed && !t.at_ozpanel_default).length;

  const columns: DataListColumn<Tunable>[] = [
    {
      key: "label",
      label: "Setting",
      sortable: true,
      render: (t) => (
        <div>
          <div className="text-[12.5px]">{t.label}</div>
          <code className="font-mono text-[11px] text-muted">{t.key}</code>
        </div>
      ),
    },
    { key: "group", label: "Group", sortable: true },
    {
      key: "live",
      label: "In force (kernel)",
      sortable: true,
      value: (t) => t.live,
      // The authoritative column. It is first of the two on purpose: what the kernel is doing
      // is the fact, and what we wrote is only an intention.
      render: (t) =>
        t.readable ? (
          <span className="font-mono text-[12.5px]">{t.live}</span>
        ) : (
          <StatusPill tone="neutral">not on this kernel</StatusPill>
        ),
    },
    {
      key: "stored",
      label: "ozpanel wrote",
      sortable: true,
      value: (t) => t.stored ?? "",
      render: (t) =>
        t.managed ? (
          <span className="font-mono text-[12.5px]">{t.stored}</span>
        ) : (
          <span className="text-[12px] text-muted">not managed — kernel default</span>
        ),
    },
    {
      key: "in_force",
      label: "State",
      sortable: true,
      value: (t) => (!t.managed ? "unmanaged" : t.in_force ? "in force" : "NOT in force"),
      render: (t) => {
        if (!t.managed) {
          // An unmanaged key is neither in force nor broken — it is simply the kernel's own
          // default. Painting it red would bury the values that genuinely are not applied.
          return t.at_ozpanel_default ? (
            <StatusPill tone="ok">already at our default</StatusPill>
          ) : (
            <StatusPill tone="neutral">kernel default</StatusPill>
          );
        }
        return t.in_force ? (
          <StatusPill tone="ok">in force</StatusPill>
        ) : (
          <StatusPill tone="bad">written, NOT in force</StatusPill>
        );
      },
    },
    { key: "why", label: "Why it matters", value: (t) => t.why, render: (t) => <span className="text-[11.5px] leading-relaxed text-muted">{t.why}</span> },
  ];

  return (
    <div>
      <StateView loading={q.isLoading} error={q.error} errorContext="kernel tuning">
        {d && (
          <>
            <Section title="Summary">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Managed values" value={String(managed)} />
                <Stat
                  label="Not in force"
                  value={String(d.not_in_force)}
                  tone={d.not_in_force > 0 ? "warn" : undefined}
                />
                <Stat label="Away from our default" value={String(offDefault)} />
                <Stat label="Observed" value={String(d.total)} />
              </div>
              {d.not_in_force > 0 && (
                <Panel className="mt-3 border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-3 text-[12.5px]">
                  <b>{d.not_in_force} value(s) are written but not in force.</b> On a container these are
                  host-controlled and can only be set on the host itself — ozpanel still writes the file so a real
                  node picks them up at boot. Everywhere else, this means a drop-in loaded later is overriding us.
                </Panel>
              )}
              <p className="mt-3 text-[11.5px] leading-relaxed text-muted">
                Managed file: <code className="font-mono">{d.managed_file}</code>. Edit values on the module's
                Settings tab; saving applies them and reports anything the kernel refuses rather than keeping it
                silently.
              </p>
            </Section>

            <Section title="Values" className="mt-6" bodyClassName="p-0">
              <div className="p-4">
                <DataList
                  items={items}
                  columns={columns}
                  getRowKey={(t) => t.key}
                  searchable
                  searchPlaceholder="Search settings…"
                  emptyMessage="No tunables reported."
                />
                {d.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{d.note}</p>}
              </div>
            </Section>
          </>
        )}
      </StateView>
    </div>
  );
}
